import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

export interface SidebarItem {
    to: string
    icon: string
    label: string
    requiredPermission?: string
    badge?: number
}

export interface SidebarSection {
    title: string
    items: SidebarItem[]
}

export type AccentColor = 'primary' | 'blue' | 'green' | 'purple' | 'orange'

export interface SidebarProps {
    sections: SidebarSection[]
    logo?: string
    logoIcon?: string
    title: string
    accentColor?: AccentColor
    isOpen?: boolean
    onClose?: () => void
}

const accentColorClasses: Record<AccentColor, { bg: string; text: string; border: string; icon: string }> = {
    primary: {
        bg: 'bg-red-50 dark:bg-primary/10',
        text: 'text-primary font-semibold',
        border: 'border-l-[3px] border-primary',
        icon: 'text-primary'
    },
    blue: {
        bg: 'bg-blue-50 dark:bg-blue-500/10',
        text: 'text-blue-700 dark:text-blue-400 font-bold',
        border: 'border-l-[3px] border-blue-600 dark:border-blue-500',
        icon: 'text-blue-600 dark:text-blue-400'
    },
    green: {
        bg: 'bg-emerald-50 dark:bg-emerald-500/10',
        text: 'text-emerald-700 dark:text-emerald-400 font-bold',
        border: 'border-l-[3px] border-emerald-600 dark:border-emerald-500',
        icon: 'text-emerald-600 dark:text-emerald-400'
    },
    purple: {
        bg: 'bg-purple-50 dark:bg-purple-500/10',
        text: 'text-purple-700 dark:text-purple-400 font-bold',
        border: 'border-l-[3px] border-purple-600 dark:border-purple-500',
        icon: 'text-purple-600 dark:text-purple-400'
    },
    orange: {
        bg: 'bg-orange-50 dark:bg-orange-500/10',
        text: 'text-orange-700 dark:text-orange-400 font-bold',
        border: 'border-l-[3px] border-orange-600 dark:border-orange-500',
        icon: 'text-orange-600 dark:text-orange-400'
    },
}

export const Sidebar: React.FC<SidebarProps> = ({
    sections,
    logo,
    logoIcon,
    title,
    accentColor = 'primary',
    isOpen = false,
    onClose
}) => {
    const colors = accentColorClasses[accentColor]
    const navigate = useNavigate()
    const { logout } = useAuth()

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    const handleNavClick = () => {
        if (onClose) onClose()
    }

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed left-0 top-0 h-screen w-64 bg-white dark:bg-surface-dark/95 backdrop-blur-xl border-r border-slate-100 dark:border-none flex flex-col z-50
                transform transition-transform duration-300 ease-in-out
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                md:translate-x-0
            `}>
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-6 border-b border-gray-100 dark:border-white/5">
                    <div className="flex items-center gap-3 min-w-0">
                        {logoIcon ? (
                            <div className="size-8 rounded-lg bg-slate-900 dark:bg-white flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-white dark:text-slate-900 text-[18px]">{logoIcon}</span>
                            </div>
                        ) : (
                            <img src={logo || '/logo.png'} alt="" className="size-8 rounded-lg flex-shrink-0" />
                        )}
                        <h1 className="text-lg font-bold font-display truncate text-slate-900 dark:text-white">{title}</h1>
                    </div>
                    {/* Mobile close button */}
                    <button
                        onClick={onClose}
                        className="md:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4 px-3">
                    {sections.map((section, sIdx) => (
                        <div key={sIdx} className="mb-6">
                            <p className="px-3 mb-2 text-xs font-black text-gray-400 dark:text-text-muted tracking-tight">
                                {section.title}
                            </p>
                            <div className="flex flex-col gap-1">
                                {section.items.map((item, iIdx) => (
                                    <NavLink
                                        key={iIdx}
                                        to={item.to}
                                        end={item.to.split('/').length <= 2}
                                        onClick={handleNavClick}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${isActive
                                                ? `${colors.bg} ${colors.text} ${colors.border} shadow-sm`
                                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white font-medium'
                                            }`
                                        }
                                    >
                                        <span className={`material-symbols-outlined text-[20px] ${colors.icon}`}>{item.icon}</span>
                                        <span>{item.label}</span>
                                        {item.badge !== undefined && item.badge > 0 && (
                                            <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-black text-white bg-red-500 rounded-full">
                                                {item.badge > 99 ? '99+' : item.badge}
                                            </span>
                                        )}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-white/5 flex flex-col gap-2">
                    <NavLink
                        to="/"
                        onClick={handleNavClick}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-all"
                    >
                        <span className="material-symbols-outlined text-[20px]">home</span>
                        <span>Back to Home</span>
                    </NavLink>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-all w-full text-left"
                    >
                        <span className="material-symbols-outlined text-[20px]">logout</span>
                        <span>Logout</span>
                    </button>
                </div>
            </aside>
        </>
    )
}

export default Sidebar

